import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { userApi } from "api/TenantSlug/User/api.ts";
import { AxiosError } from "axios";
import { useUser } from "hooks/useUser.ts";
import { ApiErrorResponse } from "types/models.ts";

import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import ExportResponse = App.Http.Responses.ExportResponse;
import GetUserRequest = App.Http.Requests.User.GetUserRequest;
import GetUserResponse = App.Http.Responses.User.GetUserResponse;
import GetUsersRequest = App.Http.Requests.User.GetUsersRequest;
import GetUsersResponse = App.Http.Responses.User.GetUsersResponse;
import SetCommissionGroupsToUserRequest = App.Http.Requests.User.SetCommissionGroupsToUserRequest;
import GetUserConversionsApiTokenResponse = App.Http.Responses.User.GetUserConversionsApiTokenResponse;
import StoreTenantUserRequest = App.Http.Requests.User.StoreTenantUserRequest;

// Mutation to create a new user directly within the current tenant
export const useStoreTenantUserMutation = (
    options: UseMutationOptions<GetUserResponse, AxiosError<ApiErrorResponse>, StoreTenantUserRequest> = {}
) => {
    return useMutationApi<GetUserResponse, StoreTenantUserRequest>({
        mutationKey: ["storeTenantUser"],
        mutationFn: (request) => userApi.store(request),
        ...options,
    });
};

// Query to get a list of users
export const useGetUsersQuery = (
    request: GetUsersRequest,
    options: Omit<UseQueryOptions<GetUsersResponse, AxiosError<ApiErrorResponse>, GetUsersResponse>, "queryKey"> = {}
) => {
    return useQueryApi<GetUsersResponse>({
        queryKey: ["getUsers", { ...request }],
        queryFn: () => userApi.index(request),
        ...options,
    });
};

// Query to get a specific user
export const useGetUserQuery = (userId: string, request: GetUserRequest, options = {}) => {
    return useQueryApi<GetUserResponse>({
        queryKey: ["getUser", userId, request],
        queryFn: () => userApi.show(userId, request),
        ...options,
    });
};

export const useExportUsersMutation = (
    options: UseMutationOptions<ExportResponse, AxiosError<ApiErrorResponse>, BaseExportRequest> = {}
) => {
    return useMutationApi<ExportResponse, BaseExportRequest>({
        mutationKey: ["exportUsers"],
        mutationFn: (request) => userApi.export(request),
        ...options,
    });
};

export const useSetUserCommissionGroupsMutation = (
    userId: string,
    options: UseMutationOptions<void, AxiosError<ApiErrorResponse>, SetCommissionGroupsToUserRequest> = {}
) => {
    return useMutationApi<void, SetCommissionGroupsToUserRequest>({
        mutationKey: ["setUserCommissionGroups", userId],
        mutationFn: (request) => userApi.setCommissionGroups(userId, request),
        ...options,
    });
};

export const useGetConversionApiToken = (options = {}) => {
    const tenantSlug = useUser().getActiveTenant()?.slug_name ?? null;

    return useQueryApi<GetUserConversionsApiTokenResponse>({
        queryKey: ["getConversionApiToken", tenantSlug],
        queryFn: () => userApi.getConversionApiToken(),
        enabled: !!tenantSlug,
        ...options,
    });
};

export const useGetConversionApiTokenForUser = (userId: string | undefined, options = {}) => {
    const tenantSlug = useUser().getActiveTenant()?.slug_name ?? null;

    return useQueryApi<GetUserConversionsApiTokenResponse>({
        queryKey: ["getConversionApiTokenForUser", tenantSlug, userId],
        queryFn: () => {
            if (!userId) {
                throw new Error("Missing userId for conversion API token query");
            }
            return userApi.getConversionApiTokenForUser(userId);
        },
        enabled: !!tenantSlug && !!userId,
        ...options,
    });
};

export const useRegenerateConversionApiTokenMutation = (
    options: UseMutationOptions<GetUserConversionsApiTokenResponse, AxiosError<ApiErrorResponse>, void> = {}
) => {
    return useMutationApi<GetUserConversionsApiTokenResponse, void>({
        mutationKey: ["regenerateConversionApiToken"],
        mutationFn: () => userApi.regenerateConversionApiToken(),
        ...options,
    });
};

export const useRegenerateConversionApiTokenForUserMutation = (
    options: UseMutationOptions<GetUserConversionsApiTokenResponse, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<GetUserConversionsApiTokenResponse, string>({
        mutationKey: ["regenerateConversionApiTokenForUser"],
        mutationFn: (userId) => userApi.regenerateConversionApiTokenForUser(userId),
        ...options,
    });
};
